import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Comercios y sus condiciones comerciales.
 *
 * 📍 El listado ya trae la condicion vigente y el conteo de tiendas resueltos
 * por el back: no hay que pedirlos por comercio.
 *
 * 🔴 Las condiciones se VERSIONAN, no se editan. Por eso no hay un
 * `updateTerms`: hay `createTermsVersion`, que crea la siguiente y cierra la
 * vigente. Una version que ya rige explica liquidaciones emitidas.
 */
@Injectable({ providedIn: 'root' })
export class MerchantService {
  private baseUrl: string;
  private api: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
    this.api = `${this.baseUrl}/api/merchant`;
  }

  private json = { headers: { 'content-type': 'application/json' } };

  // ── la ficha del comercio y sus adjuntos ────────────────────────────────────

  /** La ficha con su avance y sus documentos. */
  getPerfil = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/${code}/perfil`));

  /**
   * Sube un adjunto (RIF, registro mercantil, cédula del representante, logo).
   *
   * 🔴 SIN `content-type`: va `FormData` y el navegador tiene que poner él el
   * `multipart/form-data` CON su boundary. Si se lo ponemos a mano, el boundary
   * no viaja y el servidor recibe un cuerpo que no puede partir — o sea, «no
   * llegó ningún archivo» aunque el archivo esté ahí.
   *
   * El campo se llama `file`: es el que lee el controlador.
   */
  subirDocumento = async (code: string, kind: string, archivo: File, notes?: string): Promise<any> => {
    const cuerpo = new FormData();
    cuerpo.append('file', archivo);
    cuerpo.append('kind', kind);
    if (notes) cuerpo.append('notes', notes);
    return lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/documentos`, cuerpo));
  };

  borrarDocumento = async (code: string, id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.api}/${code}/documentos/${id}`));

  /**
   * PASO 2 — el ANALISTA revisa. `aprobar: true` la pasa al gerente;
   * `false` la devuelve al comercio con el motivo.
   */
  revisarFicha = async (code: string, aprobar: boolean, observaciones?: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/perfil/revisar`,
      { aprobar, observaciones }, this.json));

  /**
   * PASO 3 — el GERENTE aprueba. 🔴 Es el paso que HABILITA al comercio a
   * operar, y sólo funciona sobre una ficha que el analista ya revisó.
   */
  aprobarFicha = async (code: string, aprobar: boolean, observaciones?: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/perfil/aprobar`,
      { aprobar, observaciones }, this.json));

  // ── comercios ───────────────────────────────────────────────────────────────

  /** Listado. `search` busca en nombre, razon social, RIF y codigo. */
  getMerchants = async (filtros: { code?: string; state?: string; status?: boolean; search?: string } = {}): Promise<any> => {
    const params: string[] = [];
    if (filtros.code) params.push(`code=${encodeURIComponent(filtros.code)}`);
    if (filtros.state) params.push(`state=${filtros.state}`);
    if (filtros.status !== undefined) params.push(`status=${filtros.status}`);
    if (filtros.search) params.push(`search=${encodeURIComponent(filtros.search)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return lastValueFrom(this.httpClient.get<any>(`${this.api}${qs}`));
  };

  /** Un comercio con sus tiendas. */
  getMerchant = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/${code}`));

  // ── URL propia: el acrónimo de la dirección del portal (núcleo: routes/comercio-direccion.js) ──

  /** `{ sugerido }`: el acrónimo sugerido del nombre, ya libre (null si el nombre no da ninguno). */
  sugerirAcronimo = async (nombre: string): Promise<{ sugerido: string | null }> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/acronimo/sugerir`, { params: { nombre } }));

  /** `{ libre: true }` | `{ libre: false, motivo: 'formato' | 'reservado' | 'ocupado' }`. */
  acronimoDisponible = async (acronimo: string, code?: string): Promise<{ libre: boolean; motivo?: string }> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/acronimo/disponible/${encodeURIComponent(acronimo)}`,
      code ? { params: { code } } : {}));

  /** Cambia el acrónimo (sólo CrediTotal): el viejo redirige 30 días. Responde `{ portal }`. */
  cambiarAcronimo = async (code: string, acronimo: string): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.api}/${code}/acronimo`, { acronimo }, this.json));

  /** «Habilitar dirección propia»: sin body el núcleo sugiere (400 FALTA_ACRONIMO); con `acronimo` lo guarda y crea la dirección. */
  habilitarDireccionPropia = async (code: string, acronimo?: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/direccion-propia`, acronimo ? { acronimo } : {}, this.json));

  /** «Probar dirección»: el núcleo le pregunta a Google cómo va y responde `{ portal }`. */
  revisarDireccion = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/direccion/revisar`, {}, this.json));

  createMerchant = async (datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(this.api, datos, this.json));

  /** ⚠️ No manda `state`: el back lo rechaza. Para eso esta `setState`. */
  updateMerchant = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.api}/${code}`, datos, this.json));

  /**
   * El tema del portal del comercio (pestaña «Temas»). Va por el mismo `PUT` de la ficha, que es
   * sólo de CrediTotal; el núcleo lo valida en `helpers/tema-comercio.js` y rechaza con 400 lo
   * que no tenga la forma. `null` lo borra y el portal vuelve al Principal.
   */
  setTheme = async (code: string, theme: any): Promise<any> => this.updateMerchant(code, { theme });

  /** Los temas guardados del portal (lista de la pestaña «Temas», para todos los comercios). Sólo CrediTotal. */
  listarTemasGuardados = async (): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/temas-guardados`));
  /** 409 si el nombre ya existe. */
  guardarTemaGuardado = async (name: string, colors: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/temas-guardados`, { name, colors }, this.json));
  /** Los comercios que ya lo usan no cambian: su tema tiene los colores copiados. */
  eliminarTemaGuardado = async (id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.api}/temas-guardados/${id}`));

  /**
   * Suspender, terminar o reactivar. Exige motivo salvo para reactivar.
   * Un comercio SUSPENDIDO deja de vender, pero su cartera sigue viva y hay que
   * seguirle liquidando: el aviso viene en la respuesta.
   */
  setState = async (code: string, state: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED', reason?: string): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(`${this.api}/${code}/state`, { state, reason }, this.json));

  /** Baja logica. El back la niega si el comercio tiene tiendas o ventas. */
  deleteMerchant = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.api}/${code}`));

  // ── contactos y cuenta ──────────────────────────────────────────────────────

  setContacts = async (code: string, contacts: any[]): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.api}/${code}/contacts`, { contacts }, this.json));

  deleteContact = async (code: string, email: string): Promise<any> =>
    lastValueFrom(this.httpClient.request<any>('delete', `${this.api}/${code}/contacts`,
      { body: { email }, headers: this.json.headers }));

  /** La cuenta de liquidacion: exige numero, tipo, titular y su RIF. */
  setBankAccount = async (code: string, bank_account: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.api}/${code}/bank-account`, { bank_account }, this.json));

  // ── tiendas ─────────────────────────────────────────────────────────────────

  assignShops = async (code: string, shop_codes: string[]): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/shops`, { shop_codes }, this.json));

  unassignShops = async (code: string, shop_codes: string[]): Promise<any> =>
    lastValueFrom(this.httpClient.request<any>('delete', `${this.api}/${code}/shops`,
      { body: { shop_codes }, headers: this.json.headers }));

  // ── condiciones comerciales ─────────────────────────────────────────────────

  /** El tarifario estándar vigente (Mario, 07-oct-2026): la tabla de plazos de la que salen nombre y comisión. */
  getRateTable = async (tipo = 'COMPRA_DEUDA'): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/rate-table?tipo=${tipo}`));

  /** Todas las versiones del tarifario (la más nueva primero). */
  getRateTables = async (tipo = 'COMPRA_DEUDA'): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/rate-tables?tipo=${tipo}`));

  /** Versión nueva del tarifario: rige desde su fecha y cierra la vigente. */
  createRateTable = async (datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/rate-tables`, datos, this.json));

  /** Feriados bancarios (1.4/1.6a): los usa la fecha de pago cuando el contrato dice «hábil siguiente». */
  getHolidays = async (anio: number): Promise<any[]> =>
    lastValueFrom(this.httpClient.get<any[]>(`${this.api}/holidays?anio=${anio}`));

  createHoliday = async (datos: { date: string; name: string; scope: string }): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/holidays`, datos, this.json));

  /** Baja lógica: deja de contar para la fecha de pago y queda como historia. */
  deleteHoliday = async (id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.api}/holidays/${id}`));

  /** El historial, de la version mas nueva a la mas vieja. */
  getTerms = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/${code}/terms`));

  /** Crea la version SIGUIENTE y cierra la vigente. Nace SIN APROBAR. */
  createTermsVersion = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.api}/${code}/terms`, datos, this.json));

  /** 🔴 Temporal, para pruebas (2026-09-25): prende o apaga el cargo por uso de las ventas nuevas. */
  setUsageFee = async (code: string, cobrar: boolean): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(`${this.api}/${code}/usage-fee`, { cobrar }, this.json));

  /** Por el id de la versión (CON-11): el código se llegó a repetir y por código el servidor no sabe cuál. */
  approveTerms = async (versionId: string): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(`${this.api}/terms/${versionId}/approve`, {}, this.json));

  /** Solo una version FUTURA. El back la niega si ya rige. */
  cancelTerms = async (versionId: string): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(`${this.api}/terms/${versionId}/cancel`, {}, this.json));

  // ══════════════════════════════════════════════════════════════════════════
  // 🔴 SIN USO — PLANES Y PRODUCTOS DEL COMERCIO
  //
  // Verificado el 2026-08-20: **ninguno de estos 8 métodos lo llama ningún
  // componente**. Son el resto de las pestañas de asignación de planes y
  // productos, que se quitaron de la ficha del comercio por decisión de negocio
  // ("planes y productos, ahora no").
  //
  // ⚠️ Y no son inofensivos por estar sin usar: **las rutas del back siguen
  // vivas**. Un método que existe y compila es un método que alguien va a
  // llamar el día que necesite "algo de planes", sin saber que la decisión fue
  // no tener esa pantalla. Lo peligroso de este tipo de código no es que no
  // corra: es que corre bien y contradice una decisión.
  //
  // Se COMENTAN en vez de borrarse, igual que `models/settlement.js` en el
  // núcleo: la decisión fue "ahora no", no "nunca". Si vuelve, se descomenta —
  // pero vuelve con su pantalla, no como un método suelto.
  //
  // Las rutas que quedan vivas del otro lado, para cuando se retome:
  //   GET/POST /api/merchant/:code/plans      · PUT/DELETE /api/merchant/plans/:id
  //   GET/POST /api/merchant/:code/products   · PUT/DELETE /api/merchant/products/:id
  // ══════════════════════════════════════════════════════════════════════════
  /*
  // ══ PLANES DEL COMERCIO ═══════════════════════════════════════════════════
  // Devuelve los asignados Y los disponibles, para no tener que pedir dos veces.

  getMerchantPlans = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/plans`));

  assignPlan = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/plans`, datos));

  updateMerchantPlan = async (id: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/plans/${id}`, datos));

  removeMerchantPlan = async (id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.baseUrl}/api/merchant/plans/${id}`));

  // ══ PRODUCTOS DEL COMERCIO ════════════════════════════════════════════════

  getMerchantProducts = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/products`));

  assignProduct = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/products`, datos));

  updateMerchantProduct = async (id: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/products/${id}`, datos));

  removeMerchantProduct = async (id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.baseUrl}/api/merchant/products/${id}`));
  */
  // ── fin de lo comentado ───────────────────────────────────────────────────

  // ══ CLIENTES AFILIADOS ════════════════════════════════════════════════════
  // 🔴 Paginado en el SERVIDOR, distinto al resto de la consola: un comercio
  //    puede tener cientos de miles de clientes.

  getMerchantCustomers = async (
    code: string,
    opciones: { pagina?: number; porPagina?: number; search?: string; source?: string } = {}
  ): Promise<any> => {
    const params: string[] = [];
    if (opciones.pagina) params.push(`pagina=${opciones.pagina}`);
    if (opciones.porPagina) params.push(`porPagina=${opciones.porPagina}`);
    if (opciones.search) params.push(`search=${encodeURIComponent(opciones.search)}`);
    if (opciones.source) params.push(`source=${opciones.source}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/customers${qs}`));
  };

  /** `null` borra el sublimite (vuelve al cupo global). 0 NO es lo mismo que null. */
  setCustomerSublimit = async (id: string, quota_sublimit: number | null): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(
      `${this.baseUrl}/api/merchant/customers/${id}/sublimit`, { quota_sublimit }));

  // ══ CUENTAS DONDE SE LE LIQUIDA AL COMERCIO ═══════════════════════════════
  // Hay exactamente UNA principal: la que se usa si nadie elige otra.

  getPaymentAccounts = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts`));

  addPaymentAccount = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts`, datos));

  updatePaymentAccount = async (code: string, id: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts/${id}`, datos));

  setDefaultPaymentAccount = async (code: string, id: string): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(
      `${this.baseUrl}/api/merchant/${code}/payment-accounts/${id}/default`, {}));

  removePaymentAccount = async (code: string, id: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts/${id}`));

  /** 3.1/3.5: la verifica OTRA persona que la que la cargó, con la constancia (CTA-02). */
  verifyPaymentAccount = async (code: string, id: string, constancia: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts/${id}/verify`, { constancia }));

  rejectPaymentAccount = async (code: string, id: string, motivo: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/payment-accounts/${id}/reject`, { motivo }));

  // ══ CUENTAS POR PAGAR AL COMERCIO ═════════════════════════════════════════
  // Es un documento con saldo y vencimiento, no un período. El saldo y el
  // estado los calcula el back: acá no se recalculan nunca.

  getPayables = async (
    code: string,
    opciones: { state?: string; overdue?: boolean; desde?: string; hasta?: string } = {}
  ): Promise<any> => {
    const params: string[] = [];
    if (opciones.state) params.push(`state=${opciones.state}`);
    if (opciones.overdue) params.push('overdue=true');
    if (opciones.desde) params.push(`desde=${opciones.desde}`);
    if (opciones.hasta) params.push(`hasta=${opciones.hasta}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/payables${qs}`));
  };

  getPayable = async (id: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/payables/${id}`));

  createPayable = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/payables`, datos));

  addPayablePayment = async (id: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/payables/${id}/payments`, datos));

  removePayablePayment = async (id: string, paymentId: string): Promise<any> =>
    lastValueFrom(this.httpClient.delete<any>(
      `${this.baseUrl}/api/merchant/payables/${id}/payments/${paymentId}`));

  cancelPayable = async (id: string, reason: string): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(
      `${this.baseUrl}/api/merchant/payables/${id}/cancel`, { reason }));

  getPayablesResumen = async (): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/payables/resumen`));

  // ══ LIQUIDACIÓN — cuánto se le paga y por qué ═════════════════════════════
  // El PERÍODO sale de las condiciones comerciales: no se manda desde acá salvo
  // que se quiera pisar el del acuerdo.

  getSettlementPreview = async (
    code: string, opciones: { desde?: string; hasta?: string; dias?: number } = {}
  ): Promise<any> => {
    const params: string[] = [];
    if (opciones.desde) params.push(`desde=${opciones.desde}`);
    if (opciones.hasta) params.push(`hasta=${opciones.hasta}`);
    // Los días del plazo definen el período y la comisión: si no van, el back
    // usa el plazo marcado por omisión en el tarifario.
    if (opciones.dias) params.push(`dias=${opciones.dias}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return lastValueFrom(this.httpClient.get<any>(
      `${this.baseUrl}/api/merchant/${code}/settlement-preview${qs}`));
  };

  /** 🔴 El monto NO se manda: el servidor lo recalcula de las ventas. */
  createSettlement = async (code: string, opciones: any = {}): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(
      `${this.baseUrl}/api/merchant/${code}/settlement`, opciones));

  /**
   * En qué comercios compró un cliente.
   *
   * 🔴 Se DERIVA de las ventas: no hay un campo en el cliente que mantener
   * sincronizado. Acepta el `code` del cliente, que es lo que usa el perfil.
   */
  getComerciosDeCliente = async (personId: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(
      `${this.baseUrl}/api/merchant/customers/by-person/${personId}`));
  // ══ usuarios del comercio ═══════════════════════════════════════════════════
  //
  // 🔴 Todas cuelgan de `/:code`, no de `/api/user`. Asi la direccion ya dice de
  // que comercio es el usuario, y el guardia del back —que compara el comercio
  // del token contra el de la direccion— las protege sin nada extra. La
  // alternativa, `/api/user?merchant=X`, manda el comercio como un filtro que
  // elige quien pregunta: eso no es una garantia.

  /** El código del gerente en la caja (2026-10-08): { turno, anular }. */
  getCodigoGerente = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/codigo-gerente`));

  /** EJE-01 (Mario, 14-oct): el contacto que ve el comercio en su portal (su ejecutivo o el general). */
  getEjecutivo = async (code: string): Promise<any> => lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/ejecutivo`));
  /** EJE-01: asignar (por correo o código del usuario de CrediTotal) o, con null, quitar su ejecutivo. */
  setEjecutivo = async (code: string, usuario: string | null): Promise<any> => lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/${code}/ejecutivo`, { usuario }));
  setCodigoGerente = async (code: string, datos: { turno?: boolean; anular?: boolean }): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/${code}/codigo-gerente`, datos));

  getMerchantUsers = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/api/merchant/${code}/usuarios`));

  /** ⚠️ La respuesta trae la clave inicial. Se muestra una vez y no se guarda. */
  createMerchantUser = async (code: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/merchant/${code}/usuarios`, datos));

  updateMerchantUser = async (code: string, id: string, datos: any): Promise<any> =>
    lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/api/merchant/${code}/usuarios/${id}`, datos));

  toggleMerchantUser = async (code: string, id: string, activar: boolean): Promise<any> =>
    lastValueFrom(this.httpClient.patch<any>(
      `${this.baseUrl}/api/merchant/${code}/usuarios/${id}/estado`, { activar }));

  resetMerchantUserPassword = async (code: string, id: string): Promise<any> =>
    lastValueFrom(this.httpClient.post<any>(
      `${this.baseUrl}/api/merchant/${code}/usuarios/${id}/clave`, {}));
}